import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "@/components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  Activity,
  ArrowRight,
  Award,
  ChevronRight,
  Clock,
  Eye,
  GitBranch,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  RefreshCw,
  Rows3,
  Smile,
  Sparkles,
  Square,
  Syringe,
  TrendingUp,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/botox-treatment-mumbai";
const FILLERS_PATH = "/dermal-fillers-mumbai";
const THREAD_PATH = "/thread-lift-mumbai";
const FACELIFT_PATH = "/facelift-surgery-mumbai";
const NON_SURGICAL_PATH = "/non-surgical-treatments-in-mumbai";

const BOTOX_FAQS = [
  {
    question: "Will Botox make my face look frozen?",
    answer:
      "Not when placed correctly. A frozen, expressionless result comes from overdosing the muscles or treating in a way that eliminates necessary functional muscle activity. Dr. Jain assesses each patient's muscle activity at consultation and calibrates the dose to reduce the wrinkle-producing movement while preserving natural expression. Most patients treated with appropriate technique find that their friends and colleagues notice they look refreshed but do not know they have had treatment.",
  },
  {
    question: "How long does Botox last?",
    answer:
      "Results typically last 3 to 6 months depending on the area treated, the dose used, and the individual patient's metabolism and muscle activity. The masseter for jaw slimming lasts 6 to 9 months. Crow's feet and forehead lines typically last 3 to 4 months. With regular treatment over years, many patients find the intervals between sessions gradually extend as the muscles become less active from consistent treatment.",
  },
  {
    question: "Does Botox hurt?",
    answer:
      "The injections use very fine needles and most patients describe the sensation as a brief, mild sting at each injection site. No anesthesia is required for most treatment areas. Topical numbing cream can be applied for patients who are particularly sensitive. The procedure takes 10 to 20 minutes and patients return to normal activity immediately afterward.",
  },
  {
    question: "What are the risks of Botox treatment?",
    answer:
      "Temporary redness, mild swelling, and occasional small bruising at injection sites are the most common effects and resolve within 24 to 48 hours. Serious complications are rare but include brow ptosis, eyelid drooping, and asymmetry when the toxin spreads to adjacent muscles from incorrect placement. These complications are temporary and resolve as the effect wears off, but they can be distressing for the patient during the weeks they persist. Choosing an injector with detailed anatomical knowledge of the facial muscles significantly reduces the risk of these outcomes.",
  },
  {
    question: "Can men have Botox treatment?",
    answer:
      "Yes. Men represent a growing proportion of botulinum toxin patients. Male facial anatomy and muscle bulk differ from female, requiring dose adjustments and different injection protocols for optimal results. The aesthetic goals also differ: men typically want softening of lines rather than the lifted, smooth result that female patients often seek. Dr. Jain adjusts the approach to suit male facial anatomy and aesthetic preferences.",
  },
  {
    question: "How is Botox different from fillers?",
    answer:
      "Botulinum toxin relaxes the muscle causing a dynamic wrinkle. Dermal fillers add volume to fill a static wrinkle or restore lost tissue. The two treatments address different aspects of facial aging and are frequently used together. Botulinum toxin treats forehead lines, frown lines, and crow's feet, which are caused by muscle movement. Fillers treat nasolabial folds, lip thinning, cheek volume loss, and static wrinkles caused by volume deficit rather than muscle activity.",
  },
  {
    question: "How often do I need to repeat Botox?",
    answer:
      "Most patients have treatment every 3 to 6 months to maintain the result. Allowing the full effect to wear off completely before retreatment means the wrinkles return to their pre-treatment depth each cycle. Retreating before complete return of muscle activity, typically at 4 to 5 months, maintains the improvement more effectively and over time may reduce the dose needed per session as the muscles gradually become less active.",
  },
  {
    question: "Can Botox be used for sweating?",
    answer:
      "Yes. Hyperhidrosis, excessive sweating that is not controlled by standard antiperspirants, responds very well to botulinum toxin injected into the affected area. The underarms are the most commonly treated site. Palms and soles can also be treated with appropriate topical anesthesia before injection due to the sensitivity of these areas. Results last 6 to 9 months and the treatment significantly improves quality of life for patients with this condition.",
  },
];

const faceBodyLocation = { "@type": "AnatomicalStructure", name: "Face" };
const plasticSpecialty = "https://schema.org/PlasticSurgery";
const percutaneousType = "https://schema.org/PercutaneousProcedure";

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Botulinum toxin injection",
      description:
        "Minimally invasive botulinum toxin type A treatment for dynamic facial wrinkles and selected medical indications, with dosing and placement tailored to facial anatomy.",
      procedureType: percutaneousType,
      bodyLocation: faceBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Hyperhidrosis treatment with botulinum toxin",
      description:
        "Injection of botulinum toxin into axillary, palmar, or plantar skin to reduce excessive sweating by blocking sympathetic nerve signaling to sweat glands.",
      procedureType: percutaneousType,
      bodyLocation: { "@type": "AnatomicalStructure", name: "Skin" },
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Bruxism treatment with botulinum toxin",
      description:
        "Masseter muscle botulinum toxin to reduce clenching force and TMJ-related symptoms associated with teeth grinding.",
      procedureType: percutaneousType,
      bodyLocation: faceBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: BOTOX_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function BotoxJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Non-Surgical Treatments", href: NON_SURGICAL_PATH },
    { name: "Botox Treatment", href: "/botox-treatment-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Non-Surgical</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Botox Treatment</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Botulinum Toxin Injections for Wrinkles &amp; Medical Indications by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Botox" icon="💉" size="hero"  imageSrc={HERO.botox} imageAlt="Botox treatment"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsBotoxSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-botox-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is Botox?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Botox is the brand name for botulinum toxin type A, a purified protein that temporarily blocks the nerve signals to specific muscles. When the muscle cannot receive the signal to contract, it relaxes. The overlying skin, which was creasing with each muscle contraction, smooths out. The effect is temporary, lasting 3 to 6 months, after which the nerve connections regenerate and muscle activity gradually returns.
            </p>
            <p>
              Botulinum toxin is used for two broad categories of indication. Cosmetic applications target the facial muscles that produce dynamic wrinkles, lines that appear with expression and deepen over time with repeated muscle movement. Medical applications target muscles or glands that are producing symptoms: overactive sweat glands causing excessive sweating, the masseter muscle causing jaw pain and teeth grinding, and the frontalis and other muscles involved in chronic migraine.
            </p>
            <p>
              Dr. Vicky Jain provides botulinum toxin treatment for both cosmetic and medical indications. Treatment is planned based on individual facial anatomy, muscle activity patterns, and the specific concern being addressed. The same product produces very different results depending on how precisely it is placed and in what quantities.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Botulinum Toxin Treatment</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Botulinum toxin is not a product that can be applied uniformly to all patients. The dose per injection site, the number of injection points, and the depth of placement all vary based on the individual patient's muscle bulk, activity pattern, and the desired degree of movement reduction. The forehead requires particular care because the frontalis muscle is the only muscle that elevates the brow. Placing too much toxin in the frontalis without accounting for the corrugator and procerus muscles below it causes the brow to drop, producing the heavy-browed appearance that patients want to avoid. This does not happen with properly calibrated placement.
              </p>
              <p>
                Dr. Jain assesses muscle activity with the patient animating, not at rest, before marking injection sites. Watching how the muscles move during different expressions identifies the specific muscle bundles responsible for each wrinkle and allows precise placement that reduces the movement creating the wrinkle while preserving the natural expressiveness of the face. Natural results from botulinum toxin are not achieved by using less product. They are achieved by placing the right amount in the right location.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CosmeticAreasSection() {
  const cards = [
    {
      title: "Forehead Lines",
      icon: Rows3,
      copy: "Horizontal lines across the forehead caused by the frontalis muscle lifting the brows. Botulinum toxin reduces this muscle activity and smooths the lines. Dosing must account for the forehead's role in brow elevation to avoid brow ptosis from overtreatment.",
    },
    {
      title: "Frown Lines (Glabellar Lines)",
      icon: Minus,
      copy: "The vertical lines between the eyebrows, often called the eleven lines, are caused by the corrugator supercilii and procerus muscles drawing the brows together. These muscles have no functional lifting role, making the glabella one of the safest areas to treat with botulinum toxin.",
    },
    {
      title: "Crow's Feet",
      icon: Eye,
      copy: "Radiating lines at the outer corners of the eyes caused by the orbicularis oculi muscle contracting with smiling and squinting. Treatment softens these lines while preserving the natural squinting action of the eye. The lower eyelid component must be treated conservatively to avoid affecting eye closure.",
    },
    {
      title: "Brow Lift",
      icon: TrendingUp,
      copy: "Precise injection along the lateral brow depressors creates a small lift of the outer brow tail. This opens the eye and creates a more youthful, awake appearance without surgery. The effect is subtle and dependent on the patient's existing brow position.",
    },
    {
      title: "Jawline Slimming",
      icon: Square,
      copy: "The masseter muscle can be enlarged through teeth grinding, jaw clenching, or genetics. Botulinum toxin injected into the masseter causes the muscle to reduce in bulk over 6 to 8 weeks, producing a softer, less square lower face contour and simultaneously reducing the force of clenching. Results last 6 to 9 months.",
    },
    {
      title: "Lip Flip",
      icon: Smile,
      copy: "A small amount of toxin placed in the orbicularis oris muscle at the upper lip border causes the lip to roll slightly upward, creating the appearance of a fuller upper lip without filler. A subtle effect lasting 2 to 3 months. Suitable for patients who want a modest upper lip improvement without volume addition.",
    },
    {
      title: "Neck Bands (Platysmal Bands)",
      icon: GitBranch,
      copy: "Vertical bands down the neck are caused by the platysma muscle. Botulinum toxin relaxes these bands and softens the neck contour. The Nefertiti lift technique uses toxin along the jawline and upper neck to reduce the downward pull of the platysma on the lower face.",
    },
    {
      title: "Bunny Lines",
      icon: Sparkles,
      copy: "Lines across the nose caused by the nasalis muscle scrunch. These are often accentuated after other areas are treated. A small amount of toxin in the nasalis softens them without affecting the nasal tip.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20" data-testid="cosmetic-areas-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Cosmetic Areas Treated with Botox</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const MEDICAL_USES = [
  {
    title: "Hyperhidrosis (Excessive Sweating)",
    icon: Syringe,
    body: "Botulinum toxin injected into the skin of the axilla, palms, or soles blocks the nerve signals to sweat glands and dramatically reduces sweating in the treated area. One of the most consistently effective treatments for hyperhidrosis that has not responded to topical antiperspirants. Results last 6 to 9 months. The procedure requires multiple injection points across the affected area and takes 20 to 30 minutes.",
  },
  {
    title: "Bruxism and TMJ Pain",
    icon: Square,
    body: "Masseter botulinum toxin reduces the force of involuntary jaw clenching and teeth grinding by partially weakening the masseter muscle. This reduces the mechanical load on the teeth, jaw joints, and associated muscles, relieving the headache, jaw pain, and dental wear associated with bruxism. The cosmetic jaw slimming effect is a secondary benefit of the same treatment.",
  },
  {
    title: "Chronic Migraine",
    icon: Sparkles,
    body: "Botulinum toxin injected across the head and neck muscles as part of a specific protocol reduces the frequency and severity of chronic migraine in patients who meet the clinical criteria. This is a medical indication with established evidence and is administered on a 12-week cycle. Patients with chronic migraine, defined as 15 or more headache days per month, are assessed for this indication.",
  },
  {
    title: "Muscle Spasms",
    icon: Activity,
    body: "Botulinum toxin is used in neurology for focal muscle spasms including cervical dystonia, blepharospasm, and hemifacial spasm. These are specialist-assessed conditions where the toxin is placed precisely into the overactive muscle group under clinical guidance.",
  },
];

function MedicalUsesSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="medical-uses-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Medical Applications of Botulinum Toxin</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {MEDICAL_USES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0" aria-hidden>
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
                  <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_FILLER_ROWS = [
  {
    label: "Mechanism",
    botox: "Relaxes the facial muscle responsible for creating the wrinkle.",
    fillers: "Adds volume beneath the skin to fill the wrinkle or restore lost tissue.",
  },
  {
    label: "Best For",
    botox: "Dynamic wrinkles that appear with expression: forehead lines, frown lines, crow's feet.",
    fillers: "Static wrinkles present at rest, volume loss, lip enhancement, and structural contouring.",
  },
  {
    label: "Result Onset",
    botox: "Gradual. Initial effect at day 2 to 3. Full result at day 10 to 14.",
    fillers: "Immediate. Result visible directly after injection, with final settling at 2 to 3 days.",
  },
  {
    label: "Duration",
    botox: "3 to 6 months.",
    fillers: "6 to 18 months depending on filler type and area.",
  },
  {
    label: "Reversibility",
    botox: "Not directly reversible. Effect wears off naturally as nerve connections regenerate.",
    fillers: "Hyaluronic acid fillers are reversible with hyaluronidase enzyme.",
  },
  {
    label: "Used Together",
    botox: "Yes. Botox and fillers are frequently combined in the same session to address muscle-driven wrinkles and volume loss simultaneously.",
    fillers: "Yes. Botox and fillers are frequently combined in the same session to address muscle-driven wrinkles and volume loss simultaneously.",
  },
];

function VsFillersSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="vs-fillers-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Botox vs. Dermal Fillers: Understanding the Difference</h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Botox
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  <Link to={FILLERS_PATH} className="hover:text-gold transition-colors">
                    Dermal Fillers
                  </Link>
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_FILLER_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.botox}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.fillers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          Dermal fillers and botulinum toxin address different aspects of facial aging and are frequently combined in the same session for patients who have both volume loss and muscle-driven wrinkles. A comprehensive facial rejuvenation plan may include both treatments in a coordinated approach.
        </p>
      </div>
    </section>
  );
}

function HowPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-performed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Botox Treatment is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Treatment takes 10 to 20 minutes. No anesthesia is required in most cases. The skin is cleaned and the injection sites are marked based on the muscle activity assessment performed at the start of the consultation. A very fine needle is used for each injection. Most patients describe the injections as a brief, mild sting.
            </p>
            <p>
              The number of injection points and units per point are calibrated to each patient's muscle bulk and activity. Patients with stronger, more active muscles require higher doses than those with lighter muscle activity to achieve the same degree of movement reduction. This is why the units required and the cost varies between patients even for the same area of treatment.
            </p>
            <p>
              Patients leave the clinic immediately after treatment and return to normal activity the same day. The result begins to appear at day 2 to 3 and reaches full effect at day 10 to 14. A follow-up review at 2 weeks allows any touch-up adjustments if specific areas need additional treatment.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultsTimelineSection() {
  const milestones = [
    {
      period: "Days 2 to 3",
      description: "Initial muscle relaxation begins. First subtle improvement in the treated lines is visible.",
      icon: Monitor,
    },
    {
      period: "Days 5 to 7",
      description: "Visible smoothing of the treated wrinkles. The muscles are significantly less active.",
      icon: Sparkles,
    },
    {
      period: "Days 10 to 14",
      description:
        "Full effect achieved. The final result is assessed at this point. Touch-up injections are added if any areas need additional treatment.",
      icon: TrendingUp,
    },
    {
      period: "Months 3 to 6",
      description:
        "Effect gradually reduces as nerve connections regenerate and muscle activity returns. Maintenance treatment at this interval sustains the result.",
      icon: RefreshCw,
    },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="results-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Results Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-white/20" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-white/45" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-white mb-2">{milestone.period}</h4>
                  <p className="text-sm text-white/70 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-white/20" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-white/45 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-white mb-2">{milestone.period}</h4>
                  <p className="text-sm text-white/70 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PreventiveBotoxSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="preventive-botox-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Preventive Botox: Starting Treatment Earlier</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Dynamic wrinkles begin as lines that appear only with expression. Over years of repeated muscle movement, these lines etch into the skin as static wrinkles that are visible even at rest. Preventive botulinum toxin treatment, started before static wrinkles have fully developed, reduces the depth and permanence of wrinkle formation by limiting the repetitive muscle movement responsible for it. This approach is increasingly common in patients in their late 20s and 30s who want to slow the progression of expression lines before they become established creases.
            </p>
            <p>
              Preventive treatment uses lower doses than treatment of established wrinkles because the goal is partial, not complete, movement reduction. Some natural expression is preserved. The rationale is that wrinkles form through accumulation of movement over years. Reducing that movement by 30 to 50 percent over a decade produces significantly less wrinkle depth than unrestricted full movement, even without completely eliminating the expression.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function GoodCandidateSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="good-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Who Is a Good Candidate for Botox Treatment?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates for cosmetic botulinum toxin are adults with dynamic wrinkles, lines that appear with expression and are the direct result of muscle movement, who want non-surgical improvement. There is no minimum age. Patients in their late 20s starting preventive treatment and patients in their 60s treating established wrinkles are both appropriate candidates. Patients with realistic expectations about the temporary nature of results and the maintenance that sustains them get consistent satisfaction from treatment.
            </p>
            <p>
              Botulinum toxin is not appropriate during pregnancy or breastfeeding. Patients with neuromuscular conditions such as myasthenia gravis or Lambert-Eaton syndrome should not receive botulinum toxin. Patients on certain antibiotics that potentiate neuromuscular blockade are managed with additional caution. Patients with static wrinkles present at rest without the muscle component are better served by dermal fillers than by botulinum toxin, which targets muscle activity rather than skin volume. Dr. Jain advises directly when botulinum toxin is not the appropriate treatment for a specific concern and recommends the alternative that actually addresses it.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Botox" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {BOTOX_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`botox-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Botox Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your facial muscle activity, wrinkle patterns, and specific concerns to design a botulinum toxin plan that produces natural results for your facial anatomy. Medical indications including hyperhidrosis and bruxism are assessed as clinical concerns with treatment protocols specific to each condition. You leave with a clear understanding of what treatment involves, what the result will look like, and when to expect it.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Dermal Fillers",
      description: "Volume restoration and contouring to complement botulinum toxin for complete facial rejuvenation.",
      href: FILLERS_PATH,
    },
    {
      name: "Thread Lift",
      description: "Non-surgical structural lifting for patients with early facial laxity alongside wrinkle treatment.",
      href: THREAD_PATH,
    },
    {
      name: "Facelift",
      description: "Structural facial rejuvenation for patients with aging beyond what non-surgical treatments address.",
      href: FACELIFT_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BotoxTreatmentPage() {
  return (
    <>
      <SEO
        title="Botox Treatment | Dr. Vicky Jain"
        description="Forehead lines, frown lines, crow's feet, and jaw slimming without surgery. Dr. Vicky Jain provides precise Botox treatment. Book your consultation today."
        canonical="/botox-treatment-mumbai"
      />
      <BotoxJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsBotoxSection />
      <DrJainApproachSection />
      <CosmeticAreasSection />
      <MedicalUsesSection />
      <VsFillersSection />
      <HowPerformedSection />
      <ResultsTimelineSection />
      <PreventiveBotoxSection />
      <GoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
